import isApplePlatform from '@atlas/tool/box/browser/isApplePlatform';
import type { THotkeyCode, THotkeyData } from '@atlas/tool/box/event/hotkey'; // Only types, no circular import
import isNotFalsy from '@atlas/tool/box/isNotFalsy';

export default function stringifyHotkey(hotkeyData: THotkeyData): string | null {
    const apple = isApplePlatform();
    const osHotkey = apple ? hotkeyData.mac : hotkeyData.win;
    if (!osHotkey) return null;

    return osHotkey
        .filter(isNotFalsy)
        .map((hotkey) => {
            const keys = [
                ...(hotkey.ctrlKey ? [apple ? '^' : 'Ctrl'] : []),
                ...(hotkey.shiftKey ? [apple ? '⇧' : 'Shift'] : []),
                ...(hotkey.altKey ? [apple ? '⌥' : 'Alt'] : []),
                ...(hotkey.metaKey ? [apple ? '⌘' : 'Win'] : []),
                hotkeyDisplayMap[hotkey.which],
            ];
            return keys.join(apple ? '' : '+');
        })
        .join(' ');
}

// #region Helpers

const hotkeyDisplayMap: TStringifyHotkeyCodeMap = {
    // Alphabetical
    65: 'A',
    66: 'B',
    67: 'C',
    68: 'D',
    69: 'E',
    70: 'F',
    71: 'G',
    72: 'H',
    73: 'I',
    74: 'J',
    75: 'K',
    76: 'L',
    77: 'M',
    78: 'N',
    79: 'O',
    80: 'P',
    81: 'Q',
    82: 'R',
    83: 'S',
    84: 'T',
    85: 'U',
    86: 'V',
    87: 'W',
    88: 'X',
    89: 'Y',
    90: 'Z',

    // Numeric
    48: '0',
    49: '1',
    50: '2',
    51: '3',
    52: '4',
    53: '5',
    54: '6',
    55: '7',
    56: '8',
    57: '9',

    // Arrow
    40: '↓',
    37: '←',
    39: '→',
    38: '↑',

    // Special
    63: '?',
    219: '[',
    221: ']',
    191: '/',
    189: '-',
    190: '.',
    188: ',',
    8: '⌫',
    46: 'Del',
    13: 'Enter',
    27: 'Esc',
    32: 'Space',
    9: 'Tab',
};

// #endregion

// #region Types

type TStringifyHotkeyCodeMap = { [K in THotkeyCode]: string };

export type { THotkeyData };

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, vi, beforeAll, afterEach, afterAll } = import.meta.vitest;

    const createHotkey = (
        which: THotkeyCode,
        opts: { ctrl?: boolean; shift?: boolean; alt?: boolean; cmd?: boolean } = {},
    ): THotkeyData => ({
        mac: [
            {
                which,
                ctrlKey: !!opts?.ctrl,
                shiftKey: !!opts?.shift,
                altKey: !!opts?.alt,
                metaKey: !!opts?.cmd,
            },
        ],
        win: opts.cmd
            ? undefined
            : [{ which, ctrlKey: !!opts?.ctrl, shiftKey: !!opts?.shift, altKey: !!opts?.alt, metaKey: false }],
    });

    describe('stringifyHotkey', () => {
        beforeAll(() => {
            vi.mock('@atlas/tool/box/browser/isApplePlatform', () => {
                let _flag = false;
                return {
                    default: Object.assign(() => _flag, {
                        set(flag: boolean) {
                            _flag = flag;
                        },
                    }),
                };
            });
        });

        afterEach(() => {
            switchToWindows();
        });

        afterAll(() => {
            vi.resetAllMocks();
        });

        function switchToApple() {
            (isApplePlatform as unknown as { set: (flag: boolean) => void }).set(true);
        }

        function switchToWindows() {
            (isApplePlatform as unknown as { set: (flag: boolean) => void }).set(false);
        }

        it('results with nice Windows key combinations', () => {
            expect(stringifyHotkey(createHotkey(65))).toBe('A');
            expect(stringifyHotkey(createHotkey(66, { alt: true }))).toBe('Alt+B');
            expect(stringifyHotkey(createHotkey(67, { alt: true, shift: true }))).toBe('Shift+Alt+C');
            expect(stringifyHotkey(createHotkey(68, { alt: true, ctrl: true }))).toBe('Ctrl+Alt+D');
            expect(stringifyHotkey(createHotkey(69, { alt: true, shift: true, ctrl: true }))).toBe('Ctrl+Shift+Alt+E');
            expect(stringifyHotkey(createHotkey(70, { shift: true }))).toBe('Shift+F');
            expect(stringifyHotkey(createHotkey(71, { shift: true, ctrl: true }))).toBe('Ctrl+Shift+G');
            expect(stringifyHotkey(createHotkey(72, { ctrl: true }))).toBe('Ctrl+H');
        });

        it('results with nice Mac key combinations', () => {
            switchToApple();
            expect(stringifyHotkey(createHotkey(73))).toBe('I');
            expect(stringifyHotkey(createHotkey(74, { alt: true }))).toBe('⌥J');
            expect(stringifyHotkey(createHotkey(75, { alt: true, shift: true }))).toBe('⇧⌥K');
            expect(stringifyHotkey(createHotkey(76, { alt: true, shift: true, ctrl: true }))).toBe('^⇧⌥L');
            expect(stringifyHotkey(createHotkey(77, { alt: true, shift: true, ctrl: true, cmd: true }))).toBe('^⇧⌥⌘M');
            expect(stringifyHotkey(createHotkey(78, { alt: true, ctrl: true }))).toBe('^⌥N');
            expect(stringifyHotkey(createHotkey(79, { alt: true, ctrl: true, cmd: true }))).toBe('^⌥⌘O');
            expect(stringifyHotkey(createHotkey(80, { alt: true, cmd: true }))).toBe('⌥⌘P');
            expect(stringifyHotkey(createHotkey(81, { shift: true }))).toBe('⇧Q');
            expect(stringifyHotkey(createHotkey(82, { shift: true, ctrl: true }))).toBe('^⇧R');
            expect(stringifyHotkey(createHotkey(83, { shift: true, ctrl: true, cmd: true }))).toBe('^⇧⌘S');
            expect(stringifyHotkey(createHotkey(84, { shift: true, cmd: true }))).toBe('⇧⌘T');
            expect(stringifyHotkey(createHotkey(85, { ctrl: true }))).toBe('^U');
            expect(stringifyHotkey(createHotkey(86, { ctrl: true, cmd: true }))).toBe('^⌘V');
            expect(stringifyHotkey(createHotkey(87, { cmd: true }))).toBe('⌘W');
            // Finish the alphabet
            expect(stringifyHotkey(createHotkey(88, { cmd: true }))).toBe('⌘X');
            expect(stringifyHotkey(createHotkey(89, { cmd: true }))).toBe('⌘Y');
            expect(stringifyHotkey(createHotkey(90, { cmd: true }))).toBe('⌘Z');
        });

        it('displays numbers as it is', () => {
            expect(stringifyHotkey(createHotkey(48))).toBe('0');
            expect(stringifyHotkey(createHotkey(49))).toBe('1');
            expect(stringifyHotkey(createHotkey(50))).toBe('2');
            expect(stringifyHotkey(createHotkey(51))).toBe('3');
            expect(stringifyHotkey(createHotkey(52))).toBe('4');
            expect(stringifyHotkey(createHotkey(53))).toBe('5');
            expect(stringifyHotkey(createHotkey(54))).toBe('6');
            expect(stringifyHotkey(createHotkey(55))).toBe('7');
            expect(stringifyHotkey(createHotkey(56))).toBe('8');
            expect(stringifyHotkey(createHotkey(57))).toBe('9');
        });

        it('replaces arrow keys with actual arrows', () => {
            expect(stringifyHotkey(createHotkey(40))).toBe('↓');
            expect(stringifyHotkey(createHotkey(37))).toBe('←');
            expect(stringifyHotkey(createHotkey(39))).toBe('→');
            expect(stringifyHotkey(createHotkey(38))).toBe('↑');
        });

        it('has special representation for special keys', () => {
            expect(stringifyHotkey(createHotkey(63))).toBe('?');
            expect(stringifyHotkey(createHotkey(219))).toBe('[');
            expect(stringifyHotkey(createHotkey(221))).toBe(']');
            expect(stringifyHotkey(createHotkey(191))).toBe('/');
            expect(stringifyHotkey(createHotkey(8))).toBe('⌫');
            expect(stringifyHotkey(createHotkey(46))).toBe('Del');
            expect(stringifyHotkey(createHotkey(13))).toBe('Enter');
            expect(stringifyHotkey(createHotkey(27))).toBe('Esc');
            expect(stringifyHotkey(createHotkey(32))).toBe('Space');
        });

        it('results with undefined if platform hotkey is not defined', () => {
            expect(
                stringifyHotkey({
                    mac: [
                        {
                            which: 32,
                            ctrlKey: false,
                            shiftKey: false,
                            altKey: false,
                            metaKey: false,
                        },
                    ],
                }),
            ).toBe(null);

            switchToApple();

            expect(
                stringifyHotkey({
                    win: [
                        {
                            which: 32,
                            ctrlKey: false,
                            shiftKey: false,
                            altKey: false,
                            metaKey: false,
                        },
                    ],
                }),
            ).toBe(null);
        });
    });
}

// #endregion
